import React, { useState } from 'react'
import { colors } from './fieldTheme'
import { normalizeText } from './fieldModel'
import { photoAction, commentAction } from './fieldNotes'
import { isGap, themeGaps, objectReferenceLines, isMissingAtReference, missingLabel, referenceLabel } from './fieldReference'
import { ChevronLeft, CheckIcon, WarningIcon, NoResultIcon, CameraIcon, CommentIcon } from './FieldIcons'
import { FLOW_TYPES, Pastille, RowLink, SoftSearchField, StickyActionBar, Notice, LinkButton, PrimaryButton } from './FieldUI'
import { FieldPhotoSheet, FieldCommentSheet } from './FieldSheets'
import FieldItemRow from './FieldItemRow'

// Remplissage d'un thème, écran FieldThemeDetail (canvas W8 ; filtre Écarts actif : W8b). Décisions 25, 26 et 47.
// En-tête allégé (retour d'Alexandra du 24/10/2026) : « Thème 4 sur 5 » sur la ligne du retour,
// « M066 · Sortie » sous le titre ; seule la ligne recherche + « Écarts » reste collée en haut,
// « Tous » disparaît (filtre éteint = tout afficher).
// « Enregistrer » valide le thème et ouvre le thème suivant ; « ‹ Thèmes » permet de naviguer librement.
// Sous la liste, deux lignes séparées, photo et commentaire, chacune dans un volet par-dessus le thème
// (W19b, W19c). Dès qu'il y a du contenu, la ligne devient « Voir… » et le volet le montre en tête.

// Au-delà, le titre passe de 28 à 24 px (minimum de la charte pour un titre)
const LONG_TITLE = 20

function FilterButton({ pressed, disabled, tone, icon, label, children, onClick })
{
	const active = tone === 'gap' ? '#7F1D1D' : colors.primary
	return (
		<button
			type="button"
			aria-pressed={pressed}
			aria-label={label}
			disabled={disabled}
			onClick={onClick}
			style={{
				backgroundImage: 'none',
				backgroundColor: pressed ? active : colors.surface,
				flex: 'none',
				minHeight: 56,
				borderRadius: 999,
				border: '2px solid ' + (pressed ? active : (tone === 'gap' ? '#7F1D1D' : colors.border)),
				color: pressed ? '#FFFFFF' : (tone === 'gap' ? '#7F1D1D' : colors.primary),
				fontSize: 18,
				fontWeight: 800,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				gap: 8,
				padding: '0 16px',
				fontFamily: 'inherit',
				cursor: disabled ? 'not-allowed' : 'pointer',
				opacity: disabled ? 0.45 : 1
			}}
		>
			{icon}
			{children}
		</button>
	)
}

export default function FieldThemeDetail({ type, pitch, theme, index, total, reference, counts, nextName, commentItems, photoItems, inventoryPhotos, user, now, onCount, onFinish, onAddComment, onUpdateComment, onAddPhotos, onUpdatePhoto, onRemovePhoto, onBack })
{
	const [query, setQuery] = useState('')
	const [onlyGaps, setOnlyGaps] = useState(false)
	const [sheet, setSheet] = useState(null)
	const gaps = themeGaps(theme, counts, reference)
	const showGaps = onlyGaps && gaps > 0
	const needle = normalizeText(query)
	const matching = theme.objects.filter(object => !needle || normalizeText(object.name).includes(needle))
	const visible = showGaps ? matching.filter(object => isGap(object, counts, reference)) : matching
	const hidden = theme.objects.length - visible.length
	const absentLabel = missingLabel(reference)
	const photo = photoAction(photoItems.length)
	const comment = commentAction(commentItems.length)

	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
			<header style={{ background: colors.surface }}>
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minHeight: 56, padding: '4px 20px 0' }}>
					<button
						type="button"
						className="field-solid"
						onClick={onBack}
						style={{ backgroundImage: 'none', backgroundColor: '#E8EBF5', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 48, padding: '0 18px 0 10px', borderRadius: 999, border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', flex: 'none' }}
					>
						<ChevronLeft />
						Thèmes
					</button>
					<span style={{ fontSize: 15, fontWeight: 700, color: colors.textSecondary }}>{'Thème ' + (index + 1) + ' sur ' + total}</span>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 20px 0' }}>
					<h1 lang="fr" style={{ fontSize: theme.name.length > LONG_TITLE ? 24 : 28, fontWeight: 800, lineHeight: 1.15, color: colors.text, overflowWrap: 'break-word', hyphens: 'auto', WebkitHyphens: 'auto' }}>{theme.name}</h1>
					<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: colors.textSecondary }}>
						<Pastille type={type} size={24} illustrationSize={17} />
						{pitch.location + ' · ' + FLOW_TYPES[type].short}
					</span>
				</div>
			</header>
			<div style={{ position: 'sticky', top: 0, zIndex: 2, background: colors.surface, boxShadow: '0 6px 10px -6px rgba(11,16,51,.22)', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px 14px' }}>
				<div style={{ flex: 1, minWidth: 0 }}>
					<SoftSearchField
						id="field-theme-search"
						label={'Chercher un objet dans ' + theme.name}
						placeholder="Chercher un objet"
						value={query}
						onChange={setQuery}
						onClear={() => setQuery('')}
					/>
				</div>
				<FilterButton pressed={showGaps} disabled={gaps === 0} tone="gap" icon={showGaps ? <CheckIcon size={22} /> : <WarningIcon />} label={(showGaps ? 'Afficher tous les objets' : 'Afficher seulement les écarts') + ' (' + gaps + ')'} onClick={() => setOnlyGaps(!showGaps)}>
					{'Écarts (' + gaps + ')'}
				</FilterButton>
			</div>

			<div style={{ padding: '24px 0 0' }}>
				<p style={{ padding: '0 20px 12px', fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{referenceLabel(reference, now)}</p>
				{visible.length > 0 && (
					<div style={{ margin: '0 20px', background: colors.surface, borderRadius: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
						{visible.map((object, i) => {
							const ref = reference.counts[object.id]
							const value = counts[object.id] !== undefined ? counts[object.id] : ref
							return (
								<FieldItemRow
									key={object.id}
									object={object}
									value={value}
									reference={ref}
									referenceLines={objectReferenceLines(object, reference)}
									missingLabel={isMissingAtReference(object, reference) ? absentLabel : null}
									gap={isGap(object, counts, reference)}
									first={i === 0}
									onChange={(next) => onCount(object.id, next)}
								/>
							)
						})}
					</div>
				)}
				{visible.length === 0 && needle && (
					<Notice icon={<NoResultIcon />} title={'Aucun objet pour « ' + query.trim() + ' »'} hint={'Dans le thème ' + theme.name + '. Vérifiez l\'orthographe ou cherchez dans tous les thèmes.'} />
				)}
				{showGaps && hidden > 0 && (
					<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '16px 20px 0', textAlign: 'center' }}>
						<span style={{ fontSize: 17, fontWeight: 600, color: colors.textSecondary }}>{hidden === 1 ? '1 autre objet masqué' : hidden + ' autres objets masqués'}</span>
						<LinkButton onClick={() => setOnlyGaps(false)}>Tout afficher</LinkButton>
					</div>
				)}
				<div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '24px 20px 32px' }}>
					<RowLink icon={<CameraIcon size={26} />} title={photo.title} subtitle={photo.subtitle} onClick={() => setSheet('photo')} />
					<RowLink icon={<CommentIcon size={26} color="currentColor" />} title={comment.title} subtitle={comment.subtitle} onClick={() => setSheet('comment')} />
				</div>
			</div>

			<StickyActionBar>
				<PrimaryButton icon={<CheckIcon />} onClick={onFinish}>Enregistrer</PrimaryButton>
				<p style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary, textAlign: 'center' }}>
					{'Ensuite : ' + (nextName || 'récapitulatif')}
				</p>
			</StickyActionBar>
			{sheet === 'photo' && (
				<FieldPhotoSheet
					title={photoItems.length > 0 ? 'Photos' : 'Ajouter une photo'}
					subtitle={'Thème ' + theme.name}
					saveLabel="Enregistrer la photo"
					user={user}
					existing={photoItems}
					knownPhotos={inventoryPhotos}
					onUpdateExisting={onUpdatePhoto}
					onRemoveExisting={onRemovePhoto}
					onSave={(photos) => Promise.resolve(onAddPhotos(photos)).then(() => setSheet(null))}
					onClose={() => setSheet(null)}
				/>
			)}
			{sheet === 'comment' && (
				<FieldCommentSheet
					title={commentItems.length > 0 ? 'Commentaires' : 'Ajouter un commentaire'}
					subtitle={'Thème ' + theme.name}
					listTitle="Déjà saisi pour ce thème"
					items={commentItems}
					user={user}
					now={now}
					placeholder={'Ce que vous avez constaté dans ' + theme.name}
					saveLabel="Enregistrer le commentaire"
					onSave={(text) => Promise.resolve(onAddComment(text)).then(() => setSheet(null))}
					onUpdate={onUpdateComment}
					onClose={() => setSheet(null)}
				/>
			)}
		</div>
	)
}
